/**
 * O que faz: Define as formas de pagamento da consulta e monta o payload gravado no agendamento.
 * Onde usa: Agenda (marcar pago), modal de agendamento e métricas do dashboard.
 * Conexões: AgendamentoStatus, tabela agendamentos.
 * Tabelas: agendamentos (forma_pagamento, parcelas, pago_em, valor, status).
 * Data: 19/08/2026
 */

import { AgendamentoStatus } from '@/constants/agendamentos';

export const FORMAS_PAGAMENTO_AGENDAMENTO = [
  { value: 'pix', label: 'PIX' },
  { value: 'cartao', label: 'Cartão' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'outro', label: 'Outro' },
] as const;

export type FormaPagamentoAgendamento = (typeof FORMAS_PAGAMENTO_AGENDAMENTO)[number]['value'];

export const rotuloFormaPagamento = (forma: string | null | undefined): string => {
  const encontrada = FORMAS_PAGAMENTO_AGENDAMENTO.find(opcao => opcao.value === forma);
  return encontrada?.label ?? 'Sem forma';
};

export const ehFormaPagamentoValida = (forma: string | null | undefined): forma is FormaPagamentoAgendamento =>
  FORMAS_PAGAMENTO_AGENDAMENTO.some(opcao => opcao.value === forma);

export interface CamposPagamentoAgendamento {
  forma_pagamento: FormaPagamentoAgendamento | null;
  parcelas: number | null;
  pago_em: string | null;
}

export interface DadosPagamentoInformados {
  formaPagamento: string;
  parcelas: number | string;
  pagoEm?: Date | string | null;
}

/**
 * Monta os campos financeiros do agendamento.
 * Motivo: status pago precisa de forma e data; qualquer outro status limpa o registro
 * para o dashboard não somar um pagamento que a clínica desmarcou.
 */
export const montarCamposPagamentoAgendamento = (
  status: string,
  dados: DadosPagamentoInformados,
): CamposPagamentoAgendamento => {
  if (status !== AgendamentoStatus.PAGO) {
    return {
      forma_pagamento: null,
      parcelas: null,
      pago_em: null,
    };
  }

  const parcelasNumero = Number(dados.parcelas);
  const pagoEmDate = dados.pagoEm
    ? new Date(dados.pagoEm)
    : new Date();

  return {
    forma_pagamento: dados.formaPagamento as FormaPagamentoAgendamento,
    parcelas: Number.isFinite(parcelasNumero) && parcelasNumero >= 1 ? Math.floor(parcelasNumero) : 1,
    pago_em: Number.isNaN(pagoEmDate.getTime()) ? new Date().toISOString() : pagoEmDate.toISOString(),
  };
};

/**
 * Data usada no faturamento: quando marcou pago; se o registro antigo não tiver essa data, cai na data da consulta.
 */
export const dataDoFaturamento = (agendamento: {
  pago_em?: string | null;
  data_inicio: string;
}): Date => {
  if (agendamento.pago_em) {
    const dataPagamento = new Date(agendamento.pago_em);
    if (!Number.isNaN(dataPagamento.getTime())) return dataPagamento;
  }
  return new Date(agendamento.data_inicio);
};

export const pagamentoCaiNoPeriodo = (
  agendamento: { pago_em?: string | null; data_inicio: string },
  startDate: Date | null,
  endDate: Date | null,
): boolean => {
  const data = dataDoFaturamento(agendamento).getTime();
  if (startDate && data < startDate.getTime()) return false;
  if (endDate && data > endDate.getTime()) return false;
  return true;
};
